import { findParentNodeClosestToPos, type NodeConfig } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';

import { generateId, TIPTAP_LAYOUT_CELL_TYPE, TIPTAP_LAYOUT_TYPE } from './../../ricos-content';
import type { ExtensionProps, RicosExtension } from './../../ricos-types';
import { findNodeById } from './../../tiptap-editor/helpers';
import type { TiptapAdapterServices } from './../../tiptap-editor/initializeTiptapAdapter';
import { createRicosNodeExtension } from './../../tiptap-extensions';

import { createCellNormalizingTransactionPlugin } from './cell-normalizing';
import { layoutNodeView } from './layout-node-view';
import { layoutSelectionDecorationPlugin } from './layout-selection-decorator';
import { balanceLayoutCells, createEmptyCell, MAX_CELLS, MAX_COLUMNS } from './shared';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    [TIPTAP_LAYOUT_TYPE]: {
      selectLayout: (nodeId: string) => ReturnType;
      insertLayout: (colCount: number) => ReturnType;
      balanceLayoutCells: (nodeId: string) => ReturnType;
      setCellsColSpan: (nodeId: string, colSpans: number[]) => ReturnType;
    };
  }
}

export const layoutExtension = createRicosNodeExtension({
  name: TIPTAP_LAYOUT_TYPE,
  groups: ['custom-styles'],
  type: 'html-node',
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>,
    services: TiptapAdapterServices
  ) => {
    return {
      ...config,
      draggable: !services.context.isMobile,
      addOptions: () => {
        return { isMobile: services.context.isMobile, ...settings, ...config };
      },
    };
  },
  createExtensionConfig() {
    return {
      name: this.name,
      isolating: true,
      group: 'block',
      selectable: true,
      draggable: true,
      extendNodeSchema: () => {
        return {
          // We need to disable default drop cursor for layouts as this is handled by layout itself
          disableDropCursor: (editorView: EditorView, pos: { pos: number; inside: number }) => {
            if (pos.inside > 0) {
              const layoutNode = findParentNodeClosestToPos(
                editorView.state.doc.resolve(pos.inside),
                node => node.type?.name === TIPTAP_LAYOUT_TYPE
              )?.node;

              const isDraggingCell =
                editorView.dragging?.slice.content.firstChild?.type.name ===
                TIPTAP_LAYOUT_CELL_TYPE;

              if (layoutNode && isDraggingCell) {
                const dropCursorView: { scheduleRemoval: (timer: number) => void } =
                  // @ts-ignore TODO: find better way to remove scheduled cursor maybe we should fork it?
                  editorView.pluginViews.find(view => view.constructor.name === 'DropCursorView');

                if (dropCursorView) {
                  // When drop cursor is disabled, it will still be visible for 5000ms. It's hard
                  // coded in the drop cursor plugin, so we need a hack to remove it immediately to
                  // prevent multiple drop cursors from being visible at the same time.
                  dropCursorView.scheduleRemoval(0);
                }
                return true;
              }
              return false;
            }

            return false;
          },
        };
      },
      content: `${TIPTAP_LAYOUT_CELL_TYPE}{1,${MAX_CELLS}}`,
      parseHTML() {
        return [{ tag: 'div[data-layout-container]' }];
      },
      renderHTML() {
        return ['div', { 'data-layout-container': true }, 0];
      },
      addNodeView() {
        return layoutNodeView(
          this.options.isMobile ||
            (!this.options.showInToolbars && !this.options.showInQuickActions)
        );
      },
      addAttributes: () => ({}),
      addCommands() {
        return {
          selectLayout:
            (nodeId: string) =>
            ({ commands, tr }) => {
              const nodeWithPos = findNodeById(tr, nodeId)[0];
              return commands.setNodeSelection(nodeWithPos.pos);
            },
          balanceLayoutCells:
            (nodeId: string) =>
            ({ tr, dispatch }) => {
              const { node, pos } = findNodeById(tr, nodeId)[0];

              balanceLayoutCells(tr, node, pos);

              if (dispatch) {
                return dispatch(tr);
              }

              return true;
            },
          setCellsColSpan:
            (nodeId: string, colSpans: number[]) =>
            ({ tr, dispatch }) => {
              const { node, pos } = findNodeById(tr, nodeId)[0];

              node?.forEach((_child, childPos, index) => {
                tr.setNodeAttribute(pos + childPos + 1, 'colSpan', colSpans[index]);
              });

              if (dispatch) {
                return dispatch(tr);
              }

              return true;
            },
          insertLayout:
            (colCount: number) =>
            ({ chain, state }) => {
              const content = Array.from({ length: colCount }, () =>
                createEmptyCell(MAX_COLUMNS / colCount)
              );

              const range = { from: state.selection.from, to: state.selection.to };
              const layoutNode = findParentNodeClosestToPos(
                state.doc.resolve(state.selection.from),
                node => node.type?.name === TIPTAP_LAYOUT_TYPE
              );

              if (layoutNode) {
                range.from = layoutNode.pos + layoutNode.node.nodeSize;
                range.to = layoutNode.pos + layoutNode.node.nodeSize;
              }

              return chain()
                .focus()
                .insertContentAt(range, [
                  { type: 'PARAGRAPH', attrs: { id: generateId() } },
                  {
                    type: TIPTAP_LAYOUT_TYPE,
                    attrs: { id: generateId() },
                    content,
                  },
                  { type: 'PARAGRAPH', attrs: { id: generateId() } },
                ])
                .command(({ commands, dispatch, tr }) => {
                  const nodeWithPos = findNodeById(tr, content[0].attrs.id)[0] || {};
                  if (dispatch && nodeWithPos) {
                    return dispatch(commands.setTextSelection(nodeWithPos.pos as number));
                  }
                })
                .run();
            },
        };
      },
      addProseMirrorPlugins() {
        return [layoutSelectionDecorationPlugin(), createCellNormalizingTransactionPlugin()];
      },
    };
  },
});
